<template>
  <div class="page-wrap">
    <div class="page-inner-wrap">
      <close-btn :moduleName="moduleName"></close-btn>
      <page-title
        :moduleName="moduleName"
        :title="moduleTitle"
        @setShowTitle="isShortHigh = true"
        class="page-title-home"
      ></page-title>
      <div class="left">
        <article-img-swiper
          :swiperList="swiperList"
          :emptyDefaultImg="defaultBannerImg"
          :source="pageSourceType.swiper"
          :moduleName="moduleName"
          v-if="moduleConfig.BANNER"
        ></article-img-swiper>
        <class-flat
          :classList="classList"
          :source="pageSourceType.homeClass"
          v-if="moduleConfig.SHOW_CLASS"
        ></class-flat>
      </div>
      <div class="right">
        <p class="news-title" :class="{ mt: !moduleConfig.SHOW_CLASS }">
          {{ getText("recommendNews") }}
        </p>
        <article-lists
          class="home-article-lists"
          :class="{ 'short-high': isShortHigh }"
          v-if="articleListConfig.list.length > 0"
          :articleList="articleListConfig.list"
          :showLikes="moduleConfig.SHOW_LIKES"
          :articleType="moduleConfig.LIST_TYPE"
          :source="pageSourceType.recommendList"
          :moduleName="moduleName"
          @loadMore="loadMoreArticle"
          :hasMore="articleListConfig.nowPage < articleListConfig.totalPage"
        ></article-lists>
      </div>
    </div>
    <nav-bar></nav-bar>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, reactive } from "vue";
import PageTitle from "@components/common/PageTitle.vue";
import CloseBtn from "@components/common/CloseBtn.vue";
import ArticleImgSwiper from "@components/article/ArticleImgSwiper.vue";
import ArticleLists from "@components/article/ArticleLists.vue";
import ClassFlat from "@components/class/ClassFlat.vue";
import NavBar from "@components/common/NavBar.vue";
import { getIco, getText } from "@config/siteConfig";
import { useBaseStore } from "@store/base";
import { useArticleStore } from "@store/article";
import { getWebsiteClass } from "@api/base";
import { getArticleList, getSiteSwiper } from "@api/article";
import useModule from "@hooks/useModule";
import useLoading from "@hooks/useLoading";
import usePointViewPage from "@hooks/usePointViewPage";
const { setLoadingState } = useLoading();
const { moduleName, moduleConfig, moduleTitle } = useModule();

usePointViewPage({ pageKey: pageType.homePage, moduleName });

const base = useBaseStore();
const article = useArticleStore();

const defaultBannerImg = computed(() => {
  return moduleConfig.value.BANNER_URL || getIco("homebanner") || "";
});
const articleListConfig = reactive({
  nowPage: 0,
  totalPage: 1,
  list: [],
});
const classList = ref([]);
const swiperList = ref([]);
const isShortHigh = ref(false);

const initArticleData = (isLoadMore?: boolean) => {
  if (isLoadMore) {
    setLoadingState(true);
  }
  return new Promise((resolve, reject) => {
    getArticleList({
      module: moduleName,
      name: base.name,
      page: articleListConfig.nowPage + 1,
      language: base.lang,
      pageSize: article.articlePageSize,
      preview: base.preview,
      sign: base.sign,
      isDetail: 0,
    })
      .then((res) => {
        if (res?.result == 1 && res?.data) {
          if (isLoadMore) {
            setLoadingState(false);
            articleListConfig.list.push(...(res?.data?.articlesInfo || []));
            articleListConfig.totalPage = res?.data?.totalPage || 1;
          } else {
            articleListConfig.list = res?.data?.articlesInfo || [];
            articleListConfig.totalPage = res?.data?.totalPage || 1;
          }
          articleListConfig.nowPage = articleListConfig.nowPage + 1;
          resolve(true);
        } else {
          reject("initArticleData接口返回错误");
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const loadMoreArticle = () => {
  initArticleData(true);
};

const initClassData = () => {
  return new Promise((resolve, reject) => {
    getWebsiteClass({
      moduleName: moduleName,
      name: base.name,
      module: moduleName,
      preview: base.preview,
      sign: base.sign,
      language: base.lang,
    })
      .then((res) => {
        if (res?.result == 1 && res?.data) {
          if (
            res.data?.classConfig &&
            Array.isArray(res.data.classConfig) &&
            res.data.classConfig.length > 0
          ) {
            classList.value = res.data.classConfig.map((item) => {
              let text = "";
              try {
                const textObj = JSON.parse(
                  item.CLASS_NAME_MULTILINGUAL || "{}"
                );

                text = textObj[base.lang] || textObj[base.defaultLang];
              } catch (_err) {
                text = "";
              }
              return {
                ...item,
                CLASS_NAME_MULTILINGUAL: text,
              };
            });
          } else {
            classList.value = [];
          }
          resolve(true);
        } else {
          reject("initClassData接口返回错误");
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const initSwiperData = () => {
  return new Promise((resolve, reject) => {
    getSiteSwiper({
      name: base.name,
      module: moduleName,
      language: base.lang,
      preview: base.preview,
      sign: base.sign,
    })
      .then((res) => {
        if (res?.result == 1 && res?.data) {
          if (
            res.data?.sliceList &&
            Array.isArray(res.data.sliceList) &&
            res.data.sliceList.length > 0
          ) {
            swiperList.value = res.data.sliceList.map((item) => {
              return {
                ...item,
                PIC_URL: item.PIC_URL ? item.PIC_URL : defaultBannerImg.value,
              };
            });
          } else {
            swiperList.value = [];
          }
          resolve(true);
        } else {
          reject("getSiteSwiper接口返回错误");
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const initPageData = async () => {
  try {
    setLoadingState(true);
    await Promise.all([initSwiperData(), initClassData(), initArticleData()]);
    setLoadingState(false);
  } catch (error) {
    console.log("error:", error);
  }
};
initPageData();
</script>

<style lang="scss" scoped>
@import "@assets/scss/dimen.scss";

.news-title {
  font-size: $fzTitle;
  color: $titlecolor;
  width: 100%;
  margin: 0 auto 30px;
  font-weight: bold;
  &.mt {
    margin-top: 20px;
  }

  @media (orientation: landscape) {
    &.mt {
      margin-top: 0;
    }
  }
  @media only screen and (min-height: 640px) and (orientation: landscape) {
    font-size: $fzTitlePc;
    margin: 0 auto calc(30px / $pcRate);
  }
}
.page-title-home {
  width: 100%;
}

@media (orientation: landscape) {
  .page-wrap {
    .page-inner-wrap {
      display: grid;
      // grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      // grid-column-gap: 30px;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-column-gap: 30px;

      .left {
        grid-row: 2;
        grid-column: 1;
      }

      .right {
        display: flex;
        flex-direction: column;
        grid-row: 2;
        grid-column: 2;
      }
    }
    .home-article-lists {
      flex-grow: 1;
      height: calc(100vmin - 115px);
      overflow-y: auto;
      margin-bottom: 0;
      &.short-high {
        height: calc(100vmin - 200px);
      }

      &:deep(.load-more) {
        position: relative;
        bottom: unset;
        left: unset;
        transform: unset;
        margin: 0 auto;
      }
    }
  }
}

@media only screen and (min-height: 640px) and (orientation: landscape) {
  .page-wrap {
    .page-inner-wrap {
      grid-column-gap: calc(30px / $pcRate);
    }
    .home-article-lists {
      height: calc(100vmin - calc(115px / $pcRate));

      &.short-high {
        height: calc(100vmin - calc(200px / $pcRate));
      }
    }
  }
}
</style>
